import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { formatApiError } from "@/lib/api";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">{label}</span>
    <input {...props} required className="mt-2 w-full h-12 bg-white/5 border border-white/15 focus:border-[#FF3B30] outline-none px-4 transition-colors" />
  </label>
);

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const { setOpen } = useCart();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (params.get("next") === "checkout" ? "/?next=checkout" : "/");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = isLogin ? await login({ email: form.email, password: form.password }) : await register(form);
      toast.success(`Welcome${isLogin ? " back" : ""}, ${u.name}`);
      nav(u.role === "admin" && isLogin && !params.get("next") ? "/admin" : "/");
      if (params.get("next") === "checkout") setOpen(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-20 rise">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold mb-2">{isLogin ? "Member access" : "Join the crew"}</p>
      <h1 className="font-display font-black uppercase tracking-tight text-5xl">{isLogin ? "Sign in" : "Create account"}</h1>
      <form onSubmit={submit} className="space-y-5 mt-10" data-testid={`${mode}-form`}>
        {!isLogin && <Field label="Name" value={form.name} onChange={on("name")} data-testid="register-name-input" />}
        <Field label="Email" type="email" value={form.email} onChange={on("email")} data-testid={`${mode}-email-input`} />
        <Field label="Password" type="password" minLength={6} value={form.password} onChange={on("password")} data-testid={`${mode}-password-input`} />
        {error && <p data-testid="auth-error" className="text-sm text-[#FF3B30]">{error}</p>}
        <button data-testid={`${mode}-form-submit`} disabled={loading}
          className="w-full h-12 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider text-lg transition-colors disabled:opacity-60">
          {loading ? "..." : isLogin ? "Sign in" : "Create account"}
        </button>
      </form>
      <div className="flex items-center gap-4 my-6">
        <span className="h-px flex-1 bg-white/10" /><span className="font-mono text-[10px] tracking-widest text-neutral-500">OR</span><span className="h-px flex-1 bg-white/10" />
      </div>
      <button data-testid="google-signin-button" onClick={googleSignIn} type="button"
        className="w-full h-12 border border-white/20 hover:bg-white hover:text-black text-white font-display uppercase font-bold tracking-wider text-lg transition-colors flex items-center justify-center gap-3">
        <svg viewBox="0 0 48 48" className="w-5 h-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
        Continue with Google
      </button>
      <p className="text-sm text-neutral-400 mt-6">
        {isLogin ? "New to ABS? " : "Already a member? "}
        <Link data-testid="auth-switch-link" to={`${isLogin ? "/register" : "/login"}${params.get("next") ? "?next=checkout" : ""}`} className="text-white underline underline-offset-4 hover:text-[#FF3B30]">
          {isLogin ? "Create an account" : "Sign in"}
        </Link>
      </p>
    </div>
  );
}
